import React, { useState, useEffect } from "react";
import styled from "styled-components";
import config from "../utils/config";
import LoadingSpinner from "./LoadingSpinner";

const Container = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 32px 24px;
  min-height: 100vh;
  background: var(--zyvgo-background, #fafafa);
`;

const Title = styled.h1`
  color: var(--zyvgo-text-primary, #222222);
  font-size: 1.75rem;
  margin: 0 0 24px 0;
  font-weight: 700;
`;

/* Error state: reddish card with wrong-mark icon */
const ErrorStateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
`;

const ErrorCard = styled.div`
  max-width: 480px;
  width: 100%;
  text-align: center;
  padding: 48px 32px;
  background: linear-gradient(180deg, #fef2f2 0%, #ffffff 100%);
  border-radius: 16px;
  border: 1px solid #fecaca;
  box-shadow: 0 4px 24px rgba(239, 68, 68, 0.08);
`;

const ErrorIcon = styled.div`
  color: #dc2626;
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
`;

const ErrorTitle = styled.h1`
  font-size: clamp(1.5rem, 4vw, 1.875rem);
  font-weight: 700;
  color: #991b1b;
  margin: 0 0 16px 0;
  letter-spacing: -0.02em;
`;

const ErrorMessage = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #b91c1c;
  margin: 0 0 32px 0;
  opacity: 0.95;
`;

const PolicyContent = styled.div`
  color: var(--zyvgo-text-primary, #222222);
  font-size: 1rem;
  line-height: 1.65;

  h2 {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 24px 0 12px 0;
    color: var(--zyvgo-text-primary, #222222);
  }

  h2:first-child {
    margin-top: 0;
  }

  p {
    margin: 0 0 12px 0;
  }

  strong {
    font-weight: 700;
  }

  ul {
    margin: 12px 0;
    padding-left: 24px;
    list-style-type: disc;
  }

  li {
    margin-bottom: 8px;
    display: list-item;
  }

  li:last-child {
    margin-bottom: 0;
  }

  hr {
    border: none;
    border-top: 1px solid var(--zyvgo-border, #e5e5e5);
    margin: 24px 0;
  }
`;

const POLICY_TYPE_LABELS = {
  terms_of_service: "Terms of Service",
  privacy_policy: "Privacy Policy",
  consent_telehealth: "Telehealth Consent",
};

/** A line that is only bold content, e.g. "**Heading Text**" (optionally wrapped in whitespace) */
const PURE_BOLD_LINE_REGEX = /^\s*(\*\*[^*]+\*\*)\s*$/;

/** Match bullet line: "- ", "* ", or "• " at start of line */
const BULLET_REGEX = /^[-*•]\s+/;

/** Match line separator: "---" (three or more dashes on their own line) */
const LINE_SEPARATOR_REGEX = /^-{3,}\s*$/;

/** Render inline text with **bold** as <strong> */
function renderInlineBold(text) {
  if (!text || typeof text !== "string") return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

/**
 * Renders policy content with:
 * - Lines starting with ## as headings (h2)
 * - Lines starting with "- ", "* ", or "• " as list items (ul/li)
 * - **text** as bold
 * - Rest as paragraphs
 */
function renderPolicyContent(rawContent) {
  if (!rawContent || typeof rawContent !== "string") return null;

  const lines = rawContent.split(/\r?\n/);
  const elements = [];
  let paragraphBuffer = [];
  let elementKey = 0;

  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return;
    const text = paragraphBuffer.join(" ");
    paragraphBuffer = [];
    elements.push(
      <p key={`p-${elementKey++}`}>{renderInlineBold(text)}</p>
    );
  };

  const flushList = (listItems) => {
    if (listItems.length === 0) return;
    const listKey = `ul-${elementKey++}`;
    elements.push(
      <ul key={listKey}>
        {listItems.map((itemText, idx) => (
          <li key={`${listKey}-${idx}`}>{renderInlineBold(itemText)}</li>
        ))}
      </ul>
    );
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (trimmed.startsWith("##")) {
      flushParagraph();
      const headingText = trimmed.replace(/^#+\s*/, "").trim();
      elements.push(
        <h2 key={`h-${elementKey++}`}>{headingText}</h2>
      );
    } else if (LINE_SEPARATOR_REGEX.test(trimmed)) {
      flushParagraph();
      elements.push(<hr key={`hr-${elementKey++}`} />);
    } else if (PURE_BOLD_LINE_REGEX.test(trimmed)) {
      // A line that is only **bold** text should always be on its own line,
      // and force the following content to start a new paragraph.
      flushParagraph();
      elements.push(
        <p key={`pb-${elementKey++}`}>{renderInlineBold(trimmed)}</p>
      );
    } else if (BULLET_REGEX.test(trimmed)) {
      flushParagraph();
      const listItems = [];
      while (i < lines.length) {
        const bulletLine = lines[i].trim();
        if (!bulletLine) break;
        if (BULLET_REGEX.test(bulletLine)) {
          listItems.push(bulletLine.replace(BULLET_REGEX, "").trim());
          i++;
        } else {
          break;
        }
      }
      flushList(listItems);
      i--; // next iteration will re-process current line if it wasn't a bullet
    } else if (trimmed) {
      paragraphBuffer.push(trimmed);
    } else {
      flushParagraph();
    }
  }
  flushParagraph();

  return elements;
}

export default function PolicyViewer({ policyType, clientId: clientIdProp }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!policyType) {
        setError("Missing policy type.");
        setLoading(false);
        return;
      }

      const allowed = ["terms_of_service", "privacy_policy", "consent_telehealth"];
      if (!allowed.includes(policyType)) {
        setError("Invalid policy type.");
        setLoading(false);
        return;
      }

      const clientId = clientIdProp || (typeof localStorage !== "undefined" ? localStorage.getItem("welliemd_client_id") : null);
      if (!clientId) {
        setError("Client not found. Open this page from the questionnaire to continue.");
        setLoading(false);
        return;
      }

      try {
        // Public endpoint: no auth; backend validates Origin for client
        const clientApiEndpoint = typeof localStorage !== "undefined" ? localStorage.getItem("welliemd_client_api_endpoint") : null;
        const apiBase = clientApiEndpoint ? `${clientApiEndpoint.replace(/\/$/, "")}/api/v1` : config.API_BASE_URL;
        const url = `${apiBase}/public/policy/${encodeURIComponent(policyType)}/?client_id=${encodeURIComponent(clientId)}`;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Origin: typeof window !== "undefined" ? window.location.origin : "",
          },
        });

        if (cancelled) return;

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error || data.message || `Failed to load policy (${res.status})`);
          setLoading(false);
          return;
        }

        const data = await res.json();
        if (data.success && data.content != null) {
          setContent(data.content);
        } else {
          setError("No content returned.");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load policy.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [policyType, clientIdProp]);

  const title = POLICY_TYPE_LABELS[policyType] || policyType;

  if (loading) {
    return (
      <Container>
        <LoadingSpinner text="Loading policy..." />
      </Container>
    );
  }

  if (error) {
    const isNotFound =
      error.toLowerCase().includes("no published policy") ||
      error.toLowerCase().includes("no policy found") ||
      error.toLowerCase().includes("not found") ||
      error.toLowerCase().includes("404");
    return (
      <ErrorStateWrapper>
        <ErrorCard>
          <ErrorIcon aria-hidden="true">
            <svg
              width="64"
              height="64"
              viewBox="0 0 64 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="32"
                cy="32"
                r="30"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                opacity="0.2"
              />
              <path
                d="M22 22l20 20M42 22L22 42"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </ErrorIcon>
          <ErrorTitle>
            {isNotFound ? "Policy not available" : "Something went wrong"}
          </ErrorTitle>
          <ErrorMessage>
            {isNotFound
              ? "This policy page is not available right now. If you need to review terms, privacy, or telehealth consent, please contact the practice or return to the questionnaire to continue."
              : "We couldn't load this page. Please try again later or return to the questionnaire to continue."}
          </ErrorMessage>
        </ErrorCard>
      </ErrorStateWrapper>
    );
  }

  return (
    <Container>
      <Title>{title}</Title>
      <PolicyContent>{renderPolicyContent(content)}</PolicyContent>
    </Container>
  );
}
